import React from 'react';
import { Briefcase, Quote } from 'lucide-react';
import { FloatingAdinkra } from '../components/Decorators';
import { RevealOnScroll } from '../hooks/useScrollReveal';

const EXPERIENCES = [
  {
    role: 'Founder & Operations Architect',
    company: 'The Merchant Hub · Independent Practice',
    period: '2026 – Present',
    summary: 'Building a portfolio of operations systems and ventures — command centers, staff platforms, and enterprise dashboards for growing African businesses.',
    metrics: [
      { value: '14', label: 'Dashboard pages shipped' },
      { value: '30+', label: 'Automated tests' },
      { value: 'PWA', label: 'Staff app live' },
    ],
    highlights: [
      'Architected and shipped the AGL Command Center — a 14-page operations dashboard with real-time KPIs and Google Sheets sync.',
      'Built a companion Staff Hub PWA used daily by frontline operators.',
      'Designed a Playwright test suite covering 30+ flows, giving leadership confidence to iterate fast.',
    ],
  },
  {
    role: 'Business Systems Analyst (Consultant)',
    company: 'Automobiles Ghana Ltd / Stoic Team Ghana',
    period: '2024 – Present',
    summary: 'Brought in to diagnose a stalling multi-branch dealership. Rebuilt the operating model from the numbers up.',
    metrics: [
      { value: '125%', label: 'Revenue growth' },
      { value: '3', label: 'Branches aligned' },
      { value: 'AI', label: 'Systems designed' },
    ],
    highlights: [
      '125% revenue growth delivered through pricing, pipeline, and data optimization.',
      'Designed AI-enabled operational systems to support multi-branch expansion.',
      'Rewrote SOPs so new hires could be productive inside a week instead of a month.',
    ],
  },
  {
    role: 'Executive Operations Lead',
    company: 'JonMon-Sacs Ghana Ltd',
    period: '2025 – 2026',
    summary: 'Ran operations for a high-stakes development pipeline with multi-million-dollar exposure and zero tolerance for compliance drift.',
    metrics: [
      { value: '$M+', label: 'Projects managed' },
      { value: '100%', label: 'Compliance rate' },
      { value: '0', label: 'Incidents' },
    ],
    highlights: [
      'Managed multi-million-dollar property and infrastructure developments end-to-end.',
      'Maintained full regulatory compliance in a high-scrutiny environment.',
      'Built the operating cadence that kept executives, contractors, and regulators aligned.',
    ],
  },
  {
    role: 'Operations Consultant',
    company: 'Baa & Bean Café · Early Projects',
    period: '2023 – 2024',
    summary: 'Early work that shaped my operating philosophy: go sit with the people doing the job before you touch the process.',
    metrics: [
      { value: '2x', label: 'Throughput' },
      { value: '40%', label: 'Waste cut' },
    ],
    highlights: [
      'Doubled daily throughput at Baa & Bean by redesigning the order flow and staffing model.',
      'Cut waste by ~40% with a simple prep-to-demand tracking system.',
    ],
  },
];

const TESTIMONIALS = [
  {
    quote: "Adwoa is a miracle worker — her diligence and innovative approach turned our operations around. Highly recommended!",
    author: "Sharon Dedo-Azu",
    title: "CEO, Baa & Bean Café",
  },
  {
    quote: "A remarkable professional with an extraordinary ability to transform complex operational challenges into streamlined, efficient systems. Her strategic thinking and execution are of the highest calibre.",
    author: "Rtd. Major General Barry Tariye Ndiomu",
    title: "Former Amnesty Administrator, Nigeria",
  },
];

const ExperiencePage = () => (
  <div className="kente-section-bg" style={{ padding: '5rem 2rem', position: 'relative' }}>
    <FloatingAdinkra count={4} color="rgba(107, 85, 32, 0.04)" />
    <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 2 }}>

      <RevealOnScroll>
        <div className="section-header">
          <h2 className="section-title">Professional Journey</h2>
          <p className="section-subtitle">
            High-trust partnerships driving operational excellence — from dealerships to development sites.
          </p>
        </div>
        <div className="kente-border"></div>
      </RevealOnScroll>

      {/* Timeline */}
      <div className="experience-timeline">
        <div className="experience-timeline-spine"></div>
        {EXPERIENCES.map((exp, idx) => (
          <RevealOnScroll key={idx} delay={idx * 150} direction={idx % 2 === 0 ? 'left' : 'right'}>
            <div className="experience-entry">
              <div className="experience-dot">
                <Briefcase size={14} />
              </div>
              <div className="experience-card-v2">
                <div className="experience-period">{exp.period}</div>
                <h3 className="experience-role">{exp.role}</h3>
                <div className="experience-company">{exp.company}</div>
                <p className="experience-summary">{exp.summary}</p>

                <div className="experience-metrics">
                  {exp.metrics.map((m) => (
                    <div key={m.label} className="metric-block">
                      <div className="metric-value">{m.value}</div>
                      <div className="metric-label">{m.label}</div>
                    </div>
                  ))}
                </div>

                <ul className="experience-highlights">
                  {exp.highlights.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        ))}
      </div>

      {/* Testimonials */}
      <RevealOnScroll>
        <div className="section-header" style={{ marginTop: '4rem' }}>
          <h3 className="section-title" style={{ fontSize: '1.6rem' }}>What Others Say</h3>
        </div>
      </RevealOnScroll>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem', marginTop: '1.5rem' }}>
        {TESTIMONIALS.map((t, idx) => (
          <RevealOnScroll key={idx} delay={idx * 150} direction={idx % 2 === 0 ? 'left' : 'right'}>
            <div className="testimonial-card-v2">
              <Quote size={28} color="var(--gold)" style={{ opacity: 0.6 }} />
              <p className="testimonial-quote">{t.quote}</p>
              <div className="testimonial-author">
                <div className="author-name">{t.author}</div>
                <div className="author-title">{t.title}</div>
              </div>
            </div>
          </RevealOnScroll>
        ))}
      </div>
    </div>
  </div>
);

export default ExperiencePage;
